import { z } from 'zod';
/**
 * Customer-category vocabulary (SPEC SOR-129).
 *
 * The six-valued account classification chosen at registration. Storage
 * identifiers are these snake_case members — deliberately NOT LegalConnect's
 * numeric codes. Display labels are fixed copy; the canonical order below is
 * the rendering order everywhere the vocabulary appears.
 */
export const CUSTOMER_CATEGORIES = [
    'law_firm_legal_department',
    'party_without_attorney',
    'insurance_company',
    'government_agency',
    'efiling_fee_waiver_pro_per',
    'affiliate_partner',
];
export const CustomerCategorySchema = z.enum(CUSTOMER_CATEGORIES);
export const CUSTOMER_CATEGORY_LABELS = {
    law_firm_legal_department: 'Law Firm/Legal Department',
    party_without_attorney: 'Party Without Attorney',
    insurance_company: 'Insurance Company',
    government_agency: 'Government Agency',
    efiling_fee_waiver_pro_per: 'eFiling Fee Waiver (Pro Per)',
    affiliate_partner: 'Affiliate Partner',
};
/**
 * The staff/admin display derivation (R18): the fixed label for a set
 * category, blank ('') when unset — never a placeholder. The setup account
 * views render every category cell through this single helper so
 * blank-when-unset cannot drift per-surface.
 */
export function customerCategoryDisplayLabel(category) {
    return category ? CUSTOMER_CATEGORY_LABELS[category] : '';
}
// Partition by construction: every category maps to exactly one usage intent
// and offeredCategories filters the canonical list, so the two offered subsets
// are disjoint, cover all six members, and preserve canonical relative order
// automatically (R1/R2).
const CATEGORY_USAGE_INTENT = {
    law_firm_legal_department: 'firm',
    party_without_attorney: 'self',
    insurance_company: 'firm',
    government_agency: 'firm',
    efiling_fee_waiver_pro_per: 'self',
    affiliate_partner: 'firm',
};
/** Categories offered under a usage intent, in canonical relative order (R1/R2). */
export function offeredCategories(usageType) {
    return CUSTOMER_CATEGORIES.filter((category) => CATEGORY_USAGE_INTENT[category] === usageType);
}
/**
 * The plan's single account-level fee-waiver derivation: true iff the category
 * is eFiling Fee Waiver (Pro Per). No second flag exists anywhere.
 */
export function isFeeWaiverCategory(category) {
    return category === 'efiling_fee_waiver_pro_per';
}
/**
 * category → isLawFirm mapping (spec R15): only the literal law-firm category
 * resolves to the law-firm pricing client type; insurance, government, and
 * affiliate all map to false.
 */
export function isLawFirmCategory(category) {
    return category === 'law_firm_legal_department';
}
/**
 * R3: the selection survives a usage-intent change only when still offered
 * under the new intent; otherwise it clears. Re-selecting the current intent
 * therefore never clears, and a firm↔self flip always does (disjoint
 * partitions), so wizard state can never hold an un-offered category.
 */
export function retainedCategoryOnIntentChange(next, current) {
    if (current === '')
        return '';
    return offeredCategories(next).includes(current) ? current : '';
}
